import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCloudFunction } from '../hooks/useCloudFunction';
import Button from '../components/Button';
import Card from '../components/Card';
import Alert from '../components/Alert';
import { trackEvent } from '../config/analytics';

const NewAuditPage = () => {
  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [auditId, setAuditId] = useState(null);

  const { userData, refreshUserData, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { execute: runAudit } = useCloudFunction('runAudit');

  useEffect(() => {
    trackEvent('new_audit_page_viewed');
  }, []);

  // When audit completes via callable function, navigate to results
  useEffect(() => {
    if (auditId && !loading) {
      trackEvent('audit_completed', { auditId });
      navigate(`/audit/${auditId}`);
    }
  }, [auditId, loading, navigate]);

  const validateUrl = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid URL (including http:// or https://)');
      return;
    }

    // Admins bypass all subscription checks
    if (!isAdmin) {
      // Check subscription
      if (!userData?.subscriptionStatus || userData.subscriptionStatus !== 'active') {
        setError('You need an active subscription to run audits');
        setTimeout(() => navigate('/subscription'), 2000);
        return;
      }

      // Check audit limits
      if (userData.auditsUsed >= userData.auditsLimit) {
        setError('You have reached your audit limit for this month');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      trackEvent('audit_initiated', { url, maxPages });

      // Use Firebase callable function instead of REST API
      const result = await runAudit({ url, maxPages });

      if (result.auditId) {
        setAuditId(result.auditId);
        await refreshUserData();
        setLoading(false); // Triggers navigation via useEffect
      }

    } catch (err) {
      console.error('Error starting audit:', err);
      setError(err.message || 'Failed to start audit. Please try again.');
      setLoading(false);
      trackEvent('audit_error', { error: err.message });
    }
  };

  // Admins can always run audits
  const canRunAudit = isAdmin || (userData?.subscriptionStatus === 'active' &&
                      userData?.auditsUsed < userData?.auditsLimit);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">New Accessibility Audit</h1>
          <p className="mt-2 text-gray-600">
            Enter your website URL to start a comprehensive WCAG 2.2 AA accessibility audit
          </p>
        </div>

        {/* Subscription Status */}
        {userData && (
          <Card className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Audits This Month</h3>
                {isAdmin ? (
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    Unlimited (Admin)
                  </p>
                ) : (
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {userData.auditsUsed || 0} / {userData.auditsLimit || 0}
                  </p>
                )}
              </div>
              {!canRunAudit && !isAdmin && (
                <Button onClick={() => navigate('/subscription')} variant="secondary">
                  Upgrade Plan
                </Button>
              )}
              {isAdmin && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                  Admin Access
                </span>
              )}
            </div>
            {!isAdmin && userData.auditsLimit > 0 && (
              <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((userData.auditsUsed / userData.auditsLimit) * 100, 100)}%` }}
                ></div>
              </div>
            )}
          </Card>
        )}

        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        {loading && auditId ? (
          <Card>
            <div className="text-center py-12">
              <div className="inline-block">
                <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-6"></div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Audit in Progress
              </h3>
              <p className="text-gray-600 mb-4">
                We're scanning your website for accessibility issues. This may take a few minutes.
              </p>
            </div>
          </Card>
        ) : (
          <Card>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL *
                </label>
                <input
                  id="url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                  disabled={loading}
                  required
                />
                <p className="mt-2 text-sm text-gray-500">
                  Enter the full URL including http:// or https://
                </p>
              </div>

              <div>
                <label htmlFor="maxPages" className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Pages to Scan
                </label>
                <select
                  id="maxPages"
                  value={maxPages}
                  onChange={(e) => setMaxPages(parseInt(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                  disabled={loading}
                >
                  <option value="5">5 pages</option>
                  <option value="10">10 pages</option>
                  <option value="25">25 pages</option>
                  <option value="50">50 pages</option>
                  <option value="100">100 pages</option>
                  {(isAdmin || userData?.subscriptionTier === 'full' || userData?.subscriptionTier === 'enterprise') && (
                    <>
                      <option value="250">250 pages</option>
                      <option value="500">500 pages</option>
                      <option value="1000">1000 pages</option>
                    </>
                  )}
                </select>
                <p className="mt-2 text-sm text-gray-500">
                  {isAdmin && 'Admin access - unlimited pages per audit'}
                  {!isAdmin && userData?.subscriptionTier === 'small' && 'Your plan supports up to 10 pages per audit'}
                  {!isAdmin && userData?.subscriptionTier === 'standard' && 'Your plan supports up to 100 pages per audit'}
                  {!isAdmin && (userData?.subscriptionTier === 'full' || userData?.subscriptionTier === 'enterprise') && 'Your plan supports up to 1000 pages per audit'}
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                <h4 className="font-semibold text-blue-900 mb-2">What we'll check:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• WCAG 2.2 Level AA compliance</li>
                  <li>• Image alt text and descriptions</li>
                  <li>• Color contrast ratios</li>
                  <li>• Keyboard navigation</li>
                  <li>• Form labels and ARIA attributes</li>
                  <li>• Heading structure and landmarks</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  loading={loading}
                  disabled={loading || !canRunAudit}
                >
                  {loading ? 'Starting Audit...' : 'Start Audit'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>

              {!canRunAudit && userData && (
                <p className="text-sm text-red-600 text-center">
                  {userData.subscriptionStatus !== 'active'
                    ? 'You need an active subscription to run audits'
                    : 'You have reached your audit limit for this month'}
                </p>
              )}
            </form>
          </Card>
        )}

        {/* Help Text */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            Audits typically complete in 5-15 minutes depending on site size.
            You'll be redirected to the results page when complete.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewAuditPage;
